import {
  Actions,
  type FormTypesKey,
  InventoryListFormFields,
  type InventoryListFormState,
} from '@openx/types/inventoryList';
import { type ChangeEvent, useCallback, useReducer } from 'react';
import { useFormContext } from 'react-hook-form';
import { useValidate } from '../../../hooks/useValidate';
import { addBulkItems } from './utils/addBulkItems';
import { dispatchValidate } from './utils/dispatchers';
import { filterAndMapItems } from './utils/filterAndMapItems';
import { getInitialData } from './utils/getInitialData';
import { reducerInventoryList } from './utils/reducerInventoryList';
import { validateBulkInput } from './utils/validateBulkInput';

export const useHandleBulkState = (type: FormTypesKey) => {
  const { getValues, setValue } = useFormContext<InventoryListFormState>();
  const [state, dispatch] = useReducer(reducerInventoryList, getInitialData(getValues(InventoryListFormFields.VALUES)));
  const validateInput = useValidate(type);

  const handleBulkValidation = useCallback(() => {
    const validationResult = validateBulkInput(state.bulkInput, state.listData, validateInput);

    dispatchValidate(dispatch, validationResult);
  }, [state.bulkInput, state.listData, validateInput]);

  const handleBulkAdd = useCallback(() => {
    const updatedValue = addBulkItems(state.validItems, getValues(InventoryListFormFields.VALUES), dispatch);

    setValue(InventoryListFormFields.VALUES, updatedValue, { shouldDirty: true, shouldValidate: true });
  }, [getValues, setValue, state.validItems]);

  const handleRemoveItem = useCallback(
    (id: string) => {
      const { filteredListData, filteredNames } = filterAndMapItems(state.listData, id);

      setValue(InventoryListFormFields.VALUES, filteredNames, { shouldDirty: true, shouldValidate: true });
      dispatch({ type: Actions.SET_LIST_DATA, payload: filteredListData });
    },
    [setValue, state.listData]
  );

  const handleInputChange = useCallback((e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    dispatch({ type: Actions.SET_BULK_INPUT, payload: e.target.value });
  }, []);

  const handleRemoveAll = useCallback(() => {
    setValue(InventoryListFormFields.VALUES, [], { shouldDirty: true, shouldValidate: true });
    dispatch({ type: Actions.SET_LIST_DATA, payload: [] });
  }, [setValue]);

  const handleBulkReplace = useCallback(
    (items: Array<string>) => {
      dispatch({ type: Actions.SET_BULK_INPUT, payload: items.join(`\n`) });

      const validationResult = validateBulkInput(items.join(`\n`), state.listData, validateInput);

      dispatchValidate(dispatch, validationResult);
    },
    [state.listData, validateInput]
  );

  return {
    state,
    dispatch,
    handleBulkValidation,
    handleBulkAdd,
    handleRemoveItem,
    handleInputChange,
    handleRemoveAll,
    handleBulkReplace,
  };
};
